import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Plus, X, Settings, Eye, EyeOff, ZoomIn, ZoomOut, Download, Upload, Save, RefreshCw, ChevronRight, ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    const cookieString = `${name}=${cookieValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    document.cookie = cookieString;
    console.log(`[Cookie] Set ${name}:`, value, '| Cookie string length:', cookieString.length);
    return true;
  } catch (e) {
    console.error(`[Cookie] Failed to set ${name}:`, e);
    return false;
  }
};

const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const value = JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
        console.log(`[Cookie] Get ${name}:`, value);
        return value;
      }
    }
    console.log(`[Cookie] ${name} not found`);
    return null;
  } catch (e) {
    console.error(`[Cookie] Failed to get ${name}:`, e);
    return null;
  }
};

export default function MapMarkerApp() {
  const defaultIslandSVG = `<svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="water" cx="50%" cy="50%">
        <stop offset="0%" style="stop-color:#4A90E2;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#2E5C8A;stop-opacity:1" />
      </radialGradient>
      <linearGradient id="sand" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:#F4E4C1;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#D4B896;stop-opacity:1" />
      </linearGradient>
      <linearGradient id="grass" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:#7CB342;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#558B2F;stop-opacity:1" />
      </linearGradient>
    </defs>
    <rect width="800" height="600" fill="url(#water)"/>
    <ellipse cx="400" cy="320" rx="280" ry="200" fill="url(#sand)"/>
    <ellipse cx="400" cy="300" rx="220" ry="150" fill="url(#grass)"/>
    <ellipse cx="420" cy="280" rx="60" ry="45" fill="#8B7355"/>
    <path d="M 380 240 Q 390 200 400 240 Q 410 200 420 240 Q 430 200 440 240" fill="#6B8E23" stroke="#4A6B1E" stroke-width="2"/>
    <circle cx="250" cy="350" r="35" fill="#8B7355"/>
    <path d="M 230 330 Q 240 300 250 330 Q 260 300 270 330" fill="#6B8E23" stroke="#4A6B1E" stroke-width="2"/>
    <circle cx="520" cy="320" r="30" fill="#8B7355"/>
    <path d="M 505 300 Q 512 275 520 300 Q 528 275 535 300" fill="#6B8E23" stroke="#4A6B1E" stroke-width="2"/>
    <path d="M 150 450 Q 180 430 210 440 T 270 450" stroke="#C2B280" stroke-width="4" fill="none" opacity="0.6"/>
    <path d="M 530 420 Q 560 410 590 425" stroke="#C2B280" stroke-width="4" fill="none" opacity="0.6"/>
  </svg>`;

  const [maps, setMaps] = useState([]);

  const [currentMapId, setCurrentMapId] = useState();
  
  const [items, setItems] = useState([]);
  
  const [markers, setMarkers] = useState([]);

  const [labels, setLabels] = useState([]);

  const [showMenu, setShowMenu] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAllMarkers, setShowAllMarkers] = useState(true);
  const [showAllLabels, setShowAllLabels] = useState(true);
  const [showItemTypes, setShowItemTypes] = useState(true);
  const [showLocations, setShowLocations] = useState(true);
  const [showBottomAd, setShowBottomAd] = useState(true);
  const [showRightAd, setShowRightAd] = useState(true);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [newItem, setNewItem] = useState({ name: '', image: '' });
  const [newMarker, setNewMarker] = useState({ itemId: '', x: '', y: '' });
  const [newMap, setNewMap] = useState({ name: '', image: '' });
  const [newLabel, setNewLabel] = useState({ text: '', x: '', y: '' });
  const [isPlacingMarker, setIsPlacingMarker] = useState(false);
  const [isPlacingLabel, setIsPlacingLabel] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  //const [apiUrl, setApiUrl] = useState('http://localhost/api'); // Hard-coded for XAMPP
  const [apiUrl, setApiUrl] = useState('https://fortnitebus.com/api'); // Hard-coded for web
  const [saveStatus, setSaveStatus] = useState('');
  const [availableMapImages, setAvailableMapImages] = useState([]);
  const [availableItemImages, setAvailableItemImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingMapId, setEditingMapId] = useState(null);
  const [editingItemName, setEditingItemName] = useState('');
  const [editingMapName, setEditingMapName] = useState('');
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [autoSave, setAutoSave] = useState(true);
  const [imageBounds, setImageBounds] = useState({ offsetX: 0, offsetY: 0, width: 100, height: 100 });

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mapImageInputRef = useRef(null);
  const itemImageInputRef = useRef(null);

  // Convert marker percentage coordinates to screen position accounting for image bounds
  const getAdjustedPosition = (x, y) => ({
    left: imageBounds.offsetX + (x / 100) * imageBounds.width,
    top: imageBounds.offsetY + (y / 100) * imageBounds.height
  });

  // Convert screen click position to marker coordinates accounting for image bounds
  const getMarkerCoordinates = (screenX, screenY) => ({
    x: ((screenX - imageBounds.offsetX) / imageBounds.width) * 100,
    y: ((screenY - imageBounds.offsetY) / imageBounds.height) * 100
  });

  const addItem = () => {
    if (newItem.name && newItem.image) {
      setItems([...items, { 
        id: Date.now(), 
        name: newItem.name, 
        image: newItem.image
      }]);
      setNewItem({ name: '', image: '' });
    }
  };

  const addMap = () => {
    if (newMap.name && newMap.image) {
      setMaps([...maps, { id: Date.now(), name: newMap.name, image: newMap.image }]);
      setNewMap({ name: '', image: '' });
    }
  };

  const addMarker = () => {
    if (newMarker.itemId && newMarker.x && newMarker.y) {
      const selectedItemId = newMarker.itemId; // Save the selected item ID
      setMarkers([...markers, {
        id: Date.now(),
        itemId: parseInt(newMarker.itemId),
        x: parseFloat(newMarker.x),
        y: parseFloat(newMarker.y),
        visible: true,
        mapId: currentMapId // Assign to current map
      }]);
      // Keep the itemId selected, only clear x and y coordinates
      setNewMarker({ itemId: selectedItemId, x: '', y: '' });
      setIsPlacingMarker(false);
    }
  };

  const addLabel = () => {
    if (newLabel.text && newLabel.x && newLabel.y) {
      setLabels([...labels, {
        id: Date.now(),
        text: newLabel.text,
        x: parseFloat(newLabel.x),
        y: parseFloat(newLabel.y),
        mapId: currentMapId,
        visible: true
      }]);
      setNewLabel({ text: '', x: '', y: '' });
      setIsPlacingLabel(false);
    }
  };

  const deleteLabel = (id) => {
    setLabels(labels.filter(l => l.id !== id));
  };

  const handleMapClick = (e) => {
    if (isPlacingMarker && mapRef.current && !isDragging) {
      const rect = mapRef.current.getBoundingClientRect();
      const screenX = ((e.clientX - rect.left) / rect.width) * 100;
      const screenY = ((e.clientY - rect.top) / rect.height) * 100;
      const coords = getMarkerCoordinates(screenX, screenY);
      setNewMarker({ ...newMarker, x: coords.x.toFixed(1), y: coords.y.toFixed(1) });
    }
    if (isPlacingLabel && mapRef.current && !isDragging) {
      const rect = mapRef.current.getBoundingClientRect();
      const screenX = ((e.clientX - rect.left) / rect.width) * 100;
      const screenY = ((e.clientY - rect.top) / rect.height) * 100;
      const coords = getMarkerCoordinates(screenX, screenY);
      setNewLabel({ ...newLabel, x: coords.x.toFixed(1), y: coords.y.toFixed(1) });
    }
  };

  const handleMarkerClick = (e, marker) => {
    if (isEditMode) {
      e.stopPropagation();
      if (isDraggingMarker && selectedMarker?.id === marker.id) {
        // Second click - drop the marker
        setIsDraggingMarker(false);
        setSelectedMarker(null);
      } else {
        // First click - grab the marker
        setSelectedMarker(marker);
        setIsDraggingMarker(true);
      }
    }
  };

  const handleMarkerDoubleClick = (e, marker) => {
    if (isEditMode) {
      e.stopPropagation();
      setSelectedMarker(null);
      setIsDraggingMarker(false);
    }
  };

  const handleMarkerRightClick = (e, marker) => {
    e.preventDefault(); // Prevent context menu
    e.stopPropagation();
  };

  const handleMarkerDrag = (e) => {
    if (isEditMode && isDraggingMarker && selectedMarker && mapRef.current) {
      const rect = mapRef.current.getBoundingClientRect();
      const screenX = ((e.clientX - rect.left) / rect.width) * 100;
      const screenY = ((e.clientY - rect.top) / rect.height) * 100;
      const { x, y } = getMarkerCoordinates(screenX, screenY);
      
      setMarkers(markers.map(m => 
        m.id === selectedMarker.id ? { ...m, x: parseFloat(x.toFixed(1)), y: parseFloat(y.toFixed(1)) } : m
      ));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsDraggingMarker(false);
  };

  const deleteSelectedMarker = () => {
    if (selectedMarker) {
      deleteMarker(selectedMarker.id);
      setSelectedMarker(null);
      setIsDraggingMarker(false);
    }
  };

  // Handle keyboard delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedMarker && isEditMode) {
        deleteSelectedMarker();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMarker, isEditMode]);

  const handleMouseDown = (e) => {
    if (!isPlacingMarker && e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prevZoom => Math.min(Math.max(1, prevZoom + delta), 3));
  };

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setLastTouchDistance(getTouchDistance(e.touches));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDistance !== null) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches);
      const delta = (newDistance - lastTouchDistance) * 0.01;
      setZoom(prevZoom => Math.min(Math.max(1, prevZoom + delta), 3));
      setLastTouchDistance(newDistance);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setLastTouchDistance(null);
    }
  };

  const zoomIn = () => {
    setZoom(prevZoom => Math.min(prevZoom + 0.2, 3));
  };

  const zoomOut = () => {
    setZoom(prevZoom => Math.max(prevZoom - 0.2, 1));
  };

  const toggleMarkerVisibility = (itemId) => {
    setMarkers(markers.map(m => 
      m.itemId === itemId && m.mapId === currentMapId ? { ...m, visible: !m.visible } : m
    ));
  };

  const toggleAllMarkers = () => {
    const newState = !showAllMarkers;
    setShowAllMarkers(newState);
    setMarkers(markers.map(m =>
      m.mapId === currentMapId ? { ...m, visible: newState } : m
    ));
  };

  const toggleAllLabels = () => {
    const newState = !showAllLabels;
    setShowAllLabels(newState);
    setLabels(labels.map(l =>
      l.mapId === currentMapId ? { ...l, visible: newState } : l
    ));
  };

  const toggleLabelVisibility = (labelId) => {
    setLabels(labels.map(l =>
      l.id === labelId ? { ...l, visible: !l.visible } : l
    ));
  };

  const deleteMarker = (id) => {
    setMarkers(markers.filter(m => m.id !== id));
  };

  const deleteItem = (id) => {
    setItems(items.filter(i => i.id !== id));
    setMarkers(markers.filter(m => m.itemId !== id));
  };

  const deleteMap = (id) => {
    if (maps.length > 1) {
      setMaps(maps.filter(m => m.id !== id));
      if (currentMapId === id) {
        setCurrentMapId(maps.find(m => m.id !== id).id);
      }
    }
  };

  const startEditingItem = (item) => {
    setEditingItemId(item.id);
    setEditingItemName(item.name);
  };

  const saveItemName = (id) => {
    if (editingItemName.trim()) {
      setItems(items.map(item =>
        item.id === id ? { ...item, name: editingItemName.trim() } : item
      ));
    }
    setEditingItemId(null);
    setEditingItemName('');
  };

  const cancelEditingItem = () => {
    setEditingItemId(null);
    setEditingItemName('');
  };

  const startEditingMap = (map) => {
    setEditingMapId(map.id);
    setEditingMapName(map.name);
  };

  const saveMapName = (id) => {
    if (editingMapName.trim()) {
      setMaps(maps.map(map =>
        map.id === id ? { ...map, name: editingMapName.trim() } : map
      ));
    }
    setEditingMapId(null);
    setEditingMapName('');
  };

  const cancelEditingMap = () => {
    setEditingMapId(null);
    setEditingMapName('');
  };

  const getItemById = (id) => items.find(i => i.id === id);
  const getCurrentMap = () => maps.find(m => m.id === currentMapId);
  
  // Force re-render when maps or currentMapId changes
  const currentMap = getCurrentMap();


  // Force image reload when map changes with timestamp to bust cache
  const [mapImageKey, setMapImageKey] = useState(Date.now());

  useEffect(() => {
    // Update key with timestamp to force complete re-render and cache bust
    setMapImageKey(Date.now());

    // Preload and validate the image
    if (currentMap?.image) {
      console.log('Preloading map image:', currentMap.image);
      const img = new Image();
      img.onload = () => {
        console.log('✓ Map image loaded successfully');
      };
      img.onerror = () => {
        console.error('✗ Failed to load map image:', currentMap.image);
      };
      img.src = currentMap.image;
    }

  }, [currentMapId, currentMap?.image]);

  // Export data to JSON file
  const exportData = () => {
    const data = {
      maps,
      items,
      markers,
      labels,
      currentMapId,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `map-data-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import data from JSON file
  const importData = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (data.maps) setMaps(data.maps);
        if (data.items) setItems(data.items);
        if (data.markers) setMarkers(data.markers);
        if (data.labels) setLabels(data.labels);
        if (data.currentMapId) setCurrentMapId(data.currentMapId);
        
        alert('Data imported successfully!');
      } catch (error) {
        alert('Error importing data. Please check the file format.');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Save data to MySQL via PHP
  const saveToDatabase = async () => {
    if (!apiUrl) {
      alert('Please set your API URL in the code (apiUrl state)');
      return;
    }

    setSaveStatus('Saving...');
    
    try {
      const response = await fetch(`${apiUrl}/save_data.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maps,
          items,
          markers,
          labels,
          currentMapId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setSaveStatus('Saved successfully!');
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('Save failed');
        alert('Error saving: ' + (result.error || result.message));
      }
    } catch (error) {
      setSaveStatus('Save failed');
      alert('Error connecting to server: ' + error.message);
    }
  };

  // Load data from MySQL via PHP
  const loadFromDatabase = async () => {
    if (!apiUrl) {
      return;
    }

    setSaveStatus('Loading...');

    try {
      const response = await fetch(`${apiUrl}/load_data.php`);
      const result = await response.json();

      console.log('Full API response:', result);

      if (result.success && result.data) {
        // Load all data first
        if (result.data.items) {
          console.log('Loaded items:', result.data.items);
          setItems(result.data.items);
        }

        if (result.data.maps) {
          console.log('Loaded maps:', result.data.maps);
          setMaps(result.data.maps);
        }

        if (result.data.markers) {
          console.log('Loaded markers:', result.data.markers);
          setMarkers(result.data.markers);
        }

        if (result.data.labels) {
          console.log('Loaded labels:', result.data.labels);
          setLabels(result.data.labels);
        }

        setSaveStatus('Loaded successfully!');
        setTimeout(() => setSaveStatus(''), 3000);

        // Return the loaded maps for the caller to use
        return result.data.maps || [];
      } else {
        setSaveStatus('');
        console.log('No data loaded or unsuccessful response');
        return [];
      }
    } catch (error) {
      setSaveStatus('');
      console.error('Load error:', error);
      return [];
    }
  };

  // Log for debugging
  useEffect(() => {
    console.log('=== Map State Changed ===');
    console.log('currentMapId:', currentMapId);
    console.log('All maps:', maps);
    console.log('Current map object:', currentMap);
    if (currentMap) {
      console.log('Map image URL:', currentMap.image);
      console.log('Image URL length:', currentMap.image?.length);
    } else {
      console.log('WARNING: currentMap is undefined!');
    }
    console.log('All items:', items);
    console.log('========================');
  }, [currentMapId, maps, items]);

  // Clustering logic - separate by item type
  const createClusters = () => {
    const visibleMarkers = markers.filter(m => m.visible && m.mapId === currentMapId);
    const clusters = [];
    const processed = new Set();
    const clusterRadius = 8 / zoom; // Adjust cluster radius based on zoom
    const shouldCluster = zoom < 1.5; // Only cluster when zoomed out

    visibleMarkers.forEach(marker => {
      if (processed.has(marker.id)) return;

      const cluster = {
        x: marker.x,
        y: marker.y,
        markers: [marker],
        itemId: marker.itemId // Track item type for cluster
      };

      // Only group into clusters if zoom is low enough
      if (shouldCluster) {
        visibleMarkers.forEach(other => {
          if (marker.id !== other.id && 
              !processed.has(other.id) && 
              marker.itemId === other.itemId) { // Only cluster same item types
            const distance = Math.sqrt(
              Math.pow(marker.x - other.x, 2) + Math.pow(marker.y - other.y, 2)
            );
            if (distance < clusterRadius) {
              cluster.markers.push(other);
              processed.add(other.id);
            }
          }
        });
      }

      processed.add(marker.id);
      
      // Calculate center of cluster
      const avgX = cluster.markers.reduce((sum, m) => sum + m.x, 0) / cluster.markers.length;
      const avgY = cluster.markers.reduce((sum, m) => sum + m.y, 0) / cluster.markers.length;
      cluster.x = avgX;
      cluster.y = avgY;
      
      clusters.push(cluster);
    });

    return clusters;
  };

  const clusters = createClusters();

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);
      return () => {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [lastTouchDistance]);

  // Calculate image bounds for proper marker positioning with 'contain'
  useEffect(() => {
    const calculateImageBounds = () => {
      if (!mapRef.current || !currentMap?.image) return;

      const container = mapRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Load the image to get its natural dimensions
      const img = new Image();
      img.onload = () => {
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const containerAspect = containerWidth / containerHeight;

        let renderedWidth, renderedHeight, offsetX, offsetY;

        if (imgAspect > containerAspect) {
          // Image is wider - will have vertical letterboxing
          renderedWidth = containerWidth;
          renderedHeight = containerWidth / imgAspect;
          offsetX = 0;
          offsetY = (containerHeight - renderedHeight) / 2;
        } else {
          // Image is taller - will have horizontal letterboxing
          renderedHeight = containerHeight;
          renderedWidth = containerHeight * imgAspect;
          offsetX = (containerWidth - renderedWidth) / 2;
          offsetY = 0;
        }

        setImageBounds({
          offsetX: (offsetX / containerWidth) * 100,
          offsetY: (offsetY / containerHeight) * 100,
          width: (renderedWidth / containerWidth) * 100,
          height: (renderedHeight / containerHeight) * 100
        });
      };
      img.src = currentMap.image;
    };

    calculateImageBounds();
    window.addEventListener('resize', calculateImageBounds);
    return () => window.removeEventListener('resize', calculateImageBounds);
  }, [currentMap?.image]);

  // Auto-load data from database on component mount (must run first)
  useEffect(() => {
    const loadData = async () => {
      // Check if user is logged in from cookies
      const savedLoginState = getCookie('isLoggedIn');
      if (savedLoginState === true) {
        console.log('✓ Restoring login state from cookie');
        setIsLoggedIn(true);
      }

      if (apiUrl) {
        // Load data from database first
        const loadedMaps = await loadFromDatabase();
        await loadAvailableImages();

        // After loading, check if there's a saved preference in cookies
        const savedCurrentMapId = getCookie('currentMapId');

        if (savedCurrentMapId !== null && savedCurrentMapId !== undefined) {
          // Verify the saved map ID exists in loaded maps
          const mapExists = loadedMaps.find(m => m.id === savedCurrentMapId);
          if (mapExists) {
            console.log('✓ Restoring currentMapId from cookie:', savedCurrentMapId);
            setCurrentMapId(savedCurrentMapId);
          } else {
            console.log('⚠ Saved map ID not found in loaded maps, using first map');
            if (loadedMaps.length > 0) {
              setCurrentMapId(loadedMaps[0].id);
            }
          }
        } else {
          // No cookie saved, use first map as default
          console.log('No saved map preference, using first available map');
          if (loadedMaps.length > 0) {
            setCurrentMapId(loadedMaps[0].id);
          }
        }

        // After all data is loaded, restore marker visibility from cookies
        // Use a small delay to ensure state has updated
        setTimeout(() => {
          const savedMarkerVisibility = getCookie('markerVisibility');
          if (savedMarkerVisibility && Array.isArray(savedMarkerVisibility)) {
            console.log('✓ Restoring marker visibility from cookies:', savedMarkerVisibility.length, 'markers');
            setMarkers(prevMarkers => {
              if (prevMarkers.length === 0) {
                console.log('⚠ No markers to restore visibility to');
                return prevMarkers;
              }
              return prevMarkers.map(marker => {
                const saved = savedMarkerVisibility.find(m => m.id === marker.id);
                if (saved) {
                  console.log(`  Marker ${marker.id}: visible = ${saved.visible}`);
                  return { ...marker, visible: saved.visible };
                }
                return marker;
              });
            });
          } else {
            console.log('No marker visibility saved in cookies');
          }
        }, 100);
      }
    };
    loadData();
  }, []); // Only run once on mount

  // Save current map to cookies whenever it changes
  useEffect(() => {
    if (currentMapId !== null && currentMapId !== undefined) {
      console.log('💾 Saving currentMapId to cookie:', currentMapId);
      setCookie('currentMapId', currentMapId);

      // Verify it was saved
      const verify = getCookie('currentMapId');
      console.log('✓ Verified cookie value:', verify);
    }
  }, [currentMapId]);

  // Save marker visibility to cookies whenever markers change
  // Use a ref to track load state - skip saving during initial load and restoration
  const markerLoadState = useRef('initial'); // States: 'initial' -> 'loading' -> 'restored' -> 'ready'

  useEffect(() => {
    if (markers.length > 0) {
      // Skip saving during initial load
      if (markerLoadState.current === 'initial') {
        console.log('Markers loaded from database, waiting for restoration...');
        markerLoadState.current = 'loading';
        return;
      }

      // Skip saving right after restoration
      if (markerLoadState.current === 'loading') {
        console.log('Skipping save - markers just restored from cookies');
        markerLoadState.current = 'restored';
        // Wait a bit before marking as ready to save
        setTimeout(() => {
          markerLoadState.current = 'ready';
          console.log('Marker visibility save enabled');
        }, 500);
        return;
      }

      // Only save after fully loaded and restored
      if (markerLoadState.current === 'ready') {
        const visibilityData = markers.map(m => ({ id: m.id, visible: m.visible }));
        setCookie('markerVisibility', visibilityData);
        console.log('💾 Saved marker visibility to cookie:', visibilityData.length, 'markers');
      }
    }
  }, [markers]);

  // Reload images when entering admin panel
  useEffect(() => {
    if (showAdmin && isLoggedIn && apiUrl) {
      loadAvailableImages();
    }
  }, [showAdmin, isLoggedIn]); // Run when admin panel opens/closes or login status changes

  // Auto-save to database when data changes (if auto-save is enabled)
  const initialLoadComplete = useRef(false);

  useEffect(() => {
    // Skip auto-save during initial load
    if (!initialLoadComplete.current) {
      if (maps.length > 0 && items.length > 0) {
        initialLoadComplete.current = true;
        console.log('Initial load complete, auto-save enabled');
      }
      return;
    }

    // Only auto-save if enabled and logged in
    if (autoSave && isLoggedIn && apiUrl) {
      console.log('Auto-save triggered');
      saveToDatabase();
    }
  }, [items, maps, markers, labels]); // Watch for changes to items, maps, markers, or labels

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (!apiUrl) {
      setLoginError('Please set API URL first');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/login.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        setIsLoggedIn(true);
        setCookie('isLoggedIn', true); // Save login state to cookie
        console.log('💾 Saved login state to cookie');
        setLoginUsername('');
        setLoginPassword('');
        setLoginError('');
      } else {
        setLoginError(result.message || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Login failed: ' + error.message);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowAdmin(false);
    // Remove login cookie by setting it to expire in the past
    document.cookie = 'isLoggedIn=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    console.log('🗑️ Cleared login cookie');
  };

  // Toggle admin panel with login check
  const toggleAdmin = () => {
    if (!showAdmin) {
      // Trying to open admin
      if (!isLoggedIn) {
        // Not logged in, show admin panel but it will show login form
        setShowAdmin(true);
      } else {
        // Already logged in, just open admin panel and reload images
        setShowAdmin(true);
        if (apiUrl) {
          loadAvailableImages();
        }
      }
    } else {
      // Closing admin panel
      setShowAdmin(false);
    }
  };

  // Load available images from server
  const loadAvailableImages = async () => {
    if (!apiUrl) return;

    try {
      const response = await fetch(`${apiUrl}/list_images.php`);
      const result = await response.json();
      
      if (result.success) {
        setAvailableMapImages(result.mapImages || []);
        setAvailableItemImages(result.itemImages || []);
      }
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  // Upload map image
  const uploadMapImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!apiUrl) {
      alert('Please set your API URL first');
      return;
    }

    setUploadStatus('Uploading map image...');

    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', 'map');

    try {
      const response = await fetch(`${apiUrl}/upload_image.php`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setUploadStatus('Map image uploaded!');
        loadAvailableImages();
        setTimeout(() => setUploadStatus(''), 3000);
      } else {
        setUploadStatus('Upload failed');
        alert('Error: ' + (result.error || result.message));
      }
    } catch (error) {
      setUploadStatus('Upload failed');
      alert('Error uploading: ' + error.message);
    }

    if (mapImageInputRef.current) {
      mapImageInputRef.current.value = '';
    }
  };

  // Upload item image
  const uploadItemImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!apiUrl) {
      alert('Please set your API URL first');
      return;
    }

    setUploadStatus('Uploading item image...');

    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', 'item');

    try {
      const response = await fetch(`${apiUrl}/upload_image.php`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setUploadStatus('Item image uploaded!');
        loadAvailableImages();
        setTimeout(() => setUploadStatus(''), 3000);
      } else {
        setUploadStatus('Upload failed');
        alert('Error: ' + (result.error || result.message));
      }
    } catch (error) {
      setUploadStatus('Upload failed');
      alert('Error uploading: ' + error.message);
    }

    if (itemImageInputRef.current) {
      itemImageInputRef.current.value = '';
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden relative bg-gray-900">
      {/* Map Container */}
      <div 
        ref={containerRef}
        className="absolute inset-0 overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => {
          handleMouseMove(e);
          handleMarkerDrag(e);
        }}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={(e) => isEditMode && e.preventDefault()}
      >
        {/* Map Background */}
        <div
          ref={mapRef}
          key={`map-${mapImageKey}`}
          className="absolute inset-0 cursor-grab active:cursor-grabbing"
          onClick={handleMapClick}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            minWidth: '100%',
            minHeight: '100%',
            width: '100%',
            height: '100%',
            ...(currentMap?.image ? {
              backgroundImage: `url(${currentMap.image})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#1f293700'
            } : {
              background: 'linear-gradient(to bottom right, #065f46, #0f766e, #0e7490)',
              backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.08) 0%, transparent 50%)',
            })
          }}
        >
          {/* Clustered Markers */}
          {clusters.map((cluster, idx) => {
            const isCluster = cluster.markers.length > 1;
            const firstMarker = cluster.markers[0];
            const item = getItemById(firstMarker.itemId);
            const isSelected = selectedMarker?.id === firstMarker.id;
            
            return (
              <div
                key={`cluster-${idx}`}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${isEditMode && !isCluster ? 'cursor-move hover:scale-150' : 'hover:scale-125'}`}
                style={{
                  left: `${getAdjustedPosition(cluster.x, cluster.y).left}%`,
                  top: `${getAdjustedPosition(cluster.x, cluster.y).top}%`,
                  zIndex: isCluster ? 10 : (isSelected ? 20 : 5)
                }}
                onClick={(e) => !isCluster && handleMarkerClick(e, firstMarker)}
                onDoubleClick={(e) => !isCluster && handleMarkerDoubleClick(e, firstMarker)}
                onContextMenu={(e) => !isCluster && handleMarkerRightClick(e, firstMarker)}
              >
                {isCluster ? (
                  <div className="relative flex items-center justify-center">
                    <img src={item.image} alt={item.name} className="object-contain drop-shadow-lg"
                      style={{
                        minWidth: '40px',
                        minHeight: '40px',
                        maxWidth: '50px',
                        maxHeight: '50px',
                        width: '50px',
                        height: '50px',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                      }}
                    />
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-yellow-400 text-lg font-bold"
                      style={{
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, -2px 0 0 #000, 2px 0 0 #000, 0 -2px 0 #000, 0 2px 0 #000'
                      }}>
                      {cluster.markers.length}
                    </div>
                  </div>
                ) : (
                  item && (
                    <div
                      className={`flex items-center justify-center relative ${isSelected ? 'ring-4 ring-yellow-400 rounded-full' : ''}`}
                      onMouseEnter={(e) => {
                        setHoveredMarker({ item, marker: firstMarker });
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top - 10 });
                      }}
                      onMouseLeave={() => setHoveredMarker(null)}
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="object-contain drop-shadow-lg pointer-events-none"
                        style={{
                          minWidth: zoom === 1 ? '40px' : '15px',
                          minHeight: zoom === 1 ? '40px' : '15px',
                          maxWidth: zoom === 1 ? '50px' : '20px',
                          maxHeight: zoom === 1 ? '50px' : '20px',
                          width: zoom === 1 ? '50px' : '20px',
                          height: zoom === 1 ? '50px' : '20px',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                        }}
                        onError={(e) => {
                          console.error('Failed to load marker image:', item.image);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )
                )}
              </div>
            );
          })}

          {/* Location Labels */}
          {labels.filter(l => l.mapId === currentMapId && l.visible).map(label => (
            <div
              key={label.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${getAdjustedPosition(label.x, label.y).left}%`,
                top: `${getAdjustedPosition(label.x, label.y).top}%`,
                zIndex: 3
              }}
            >
              <span
                className="text-white font-bold text-lg"
                style={{
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000, -3px 0 0 #000, 3px 0 0 #000, 0 -3px 0 #000, 0 3px 0 #000, -2px -1px 0 #000, 2px -1px 0 #000, -2px 1px 0 #000, 2px 1px 0 #000, -1px -2px 0 #000, 1px -2px 0 #000, -1px 2px 0 #000, 1px 2px 0 #000'
                }}
              >
                {label.text}
              </span>
            </div>
          ))}
        </div>

        {isPlacingMarker && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-black px-4 py-2 rounded-lg shadow-lg font-semibold z-50">
            Click on the map to place marker at X: {newMarker.x || '0'}, Y: {newMarker.y || '0'}
          </div>
        )}

        {isEditMode && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg font-semibold z-50 flex items-center gap-3">
            <span>Edit Mode: Click to grab • Click again to drop • Double-click or Delete key to remove</span>
            {selectedMarker && isDraggingMarker && (
              <span className="bg-yellow-500 text-black px-2 py-1 rounded text-sm">Moving...</span>
            )}
            {selectedMarker && (
              <button
                onClick={deleteSelectedMarker}
                className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded transition-colors flex items-center gap-1"
              >
                <X size={16} /> Delete
              </button>
            )}
          </div>
        )}
      </div>

      {/* Zoom Controls */}
      <div className="absolute bottom-8 right-8 flex flex-col gap-2 z-[9999]">
        <button
          onClick={zoomIn}
          className="bg-gray-800 text-white p-3 rounded-lg shadow-lg hover:bg-gray-700 transition-colors"
        >
          <ZoomIn size={24} />
        </button>
        <div className="bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg text-center font-semibold">
          {Math.round(zoom * 100)}%
        </div>
        <button
          onClick={zoomOut}
          className="bg-gray-800 text-white p-3 rounded-lg shadow-lg hover:bg-gray-700 transition-colors"
        >
          <ZoomOut size={24} />
        </button>
      </div>

      {/* Custom Tooltip */}
      {hoveredMarker && (
        <div
          className="absolute pointer-events-none z-50"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="bg-black text-white px-3 py-2 rounded-lg shadow-lg font-semibold text-sm whitespace-nowrap">
            {hoveredMarker.item.name}
            <div
              className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid #000000'
              }}
            />
          </div>
        </div>
      )}

      {/* Menu Toggle Button - Arrow on left edge */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="absolute top-1/2 left-0 -translate-y-1/2 z-30 text-white py-8 px-2 rounded-r-lg shadow-lg bg-green-600 hover:bg-green-700 transition-all"
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >
        {showMenu ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
      </button>

      {/* Side Menu */}
      <div className={`absolute left-0 top-0 h-full w-80 bg-gray-900 bg-opacity-95 shadow-2xl transform transition-transform duration-300 z-20 ${showMenu ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 h-full overflow-y-scroll custom-scrollbar">
          <div className="flex justify-between items-center mb-6">
            <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="Logo" className="h-12 w-auto" />
            <button onClick={toggleAdmin} className="text-gray-300 hover:text-white">
              <Settings size={24} />
            </button>
          </div>

          {!showAdmin ? (
            <>
              {/* Map Selector */}
              <div className="mb-6">
                <select
                  value={currentMapId}
                  onChange={(e) => setCurrentMapId(parseInt(e.target.value))}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 outline-none font-medium"
                >
                  {maps.map(map => (
                    <option key={map.id} value={map.id}>{map.name}</option>
                  ))}
                </select>
              </div>

              {/* Show All Toggle */}
              {/* Individual Toggles */}
              <div className="space-y-2">
                <div className="w-full flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleAllMarkers}
                      className={`p-2 rounded-lg transition-colors ${showAllMarkers ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                      title={showAllMarkers ? "Hide all markers" : "Show all markers"}
                    >
                      {showAllMarkers ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                    <span className="text-lg font-semibold text-white">Item Types</span>
                  </div>
                  <button
                    onClick={() => setShowItemTypes(!showItemTypes)}
                    className="text-white hover:text-blue-400 transition-colors"
                  >
                    {showItemTypes ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>
                {showItemTypes && (
                  <>
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={itemSearchQuery}
                      onChange={(e) => setItemSearchQuery(e.target.value)}
                      className="w-full p-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 outline-none text-sm"
                    />
                    {items.filter(item =>
                      item.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
                    ).map(item => {
                      const itemMarkers = markers.filter(m => m.itemId === item.id && m.mapId === currentMapId);
                      const allVisible = itemMarkers.every(m => m.visible);
                      const count = itemMarkers.length;

                      return (
                        <button
                          key={item.id}
                          onClick={() => toggleMarkerVisibility(item.id)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${allVisible ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 hover:bg-gray-700'} text-white`}
                        >
                          <div className="flex items-center gap-3">
                            <img src={item.image} alt={item.name} className="object-contain" style={{ minWidth: '30px', minHeight: '30px', maxWidth: '40px', maxHeight: '40px', width: '40px', height: '40px' }} />
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">({count})</span>
                            {allVisible ? <Eye size={18} /> : <EyeOff size={18} />}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Locations Section */}
              <div className="space-y-2 mt-6">
                <div className="w-full flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleAllLabels}
                      className={`p-2 rounded-lg transition-colors ${showAllLabels ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                      title={showAllLabels ? "Hide all locations" : "Show all locations"}
                    >
                      {showAllLabels ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                    <span className="text-lg font-semibold text-white">Locations</span>
                  </div>
                  <button
                    onClick={() => setShowLocations(!showLocations)}
                    className="text-white hover:text-blue-400 transition-colors"
                  >
                    {showLocations ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>
                {showLocations && labels.filter(l => l.mapId === currentMapId).map(label => (
                  <button
                    key={label.id}
                    onClick={() => toggleLabelVisibility(label.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${label.visible ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 hover:bg-gray-700'} text-white`}
                  >
                    <span className="font-medium">{label.text}</span>
                    {label.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                ))}
              </div>

              {/* Ad Placeholder */}
              <div className="bg-gray-800 p-4 rounded-lg flex items-center justify-center mt-4">
                <div className="bg-gray-700 flex items-center justify-center rounded">
                  <img src={`${process.env.PUBLIC_URL}/ad/300x250.jpg`} alt="Ad" className="object-fill" />
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              {!isLoggedIn ? (
                // Login Form
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-white mb-4">Admin Login</h3>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                      <input
                        type="text"
                        value={loginUsername}
                        onChange={(e) => setLoginUsername(e.target.value)}
                        className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>
                    {loginError && (
                      <p className="text-red-400 text-sm">{loginError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors font-semibold"
                    >
                      Login
                    </button>
                  </form>
                </div>
              ) : (
                // Admin Panel (only shown when logged in)
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">Admin Panel</h3>
                    <button
                      onClick={handleLogout}
                      className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors"
                    >
                      Logout
                    </button>
                  </div>
              
              {/* Database Save/Load Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Database Storage</h4>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Auto-Save</span>
                  <button
                    onClick={() => setAutoSave(!autoSave)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoSave ? 'bg-blue-600' : 'bg-gray-600'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoSave ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <button
                  onClick={saveToDatabase}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-1 transition-colors text-xs"
                >
                  <Save size={16} /> Save
                </button>
                {saveStatus && (
                  <p className="text-xs text-center text-yellow-400 font-semibold">
                    {saveStatus}
                  </p>
                )}
              </div>

              {/* Map Selection */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Select Map</h4>
                <select
                  value={currentMapId}
                  onChange={(e) => setCurrentMapId(parseInt(e.target.value))}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                >
                  {maps.map(map => (
                    <option key={map.id} value={map.id}>{map.name}</option>
                  ))}
                </select>
              </div>

              {/* Add Map Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Add New Map</h4>
                <input
                  type="text"
                  placeholder="Map name"
                  value={newMap.name}
                  onChange={(e) => setNewMap({ ...newMap, name: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <div className="space-y-2">
                  <select
                    value={newMap.image}
                    onChange={(e) => setNewMap({ ...newMap, image: e.target.value })}
                    className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                  >
                    <option value="">Select map background</option>
                    {availableMapImages.map((img, idx) => (
                      <option key={idx} value={img.url}>{img.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => mapImageInputRef.current?.click()}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors text-sm"
                  >
                    + Upload New Map Image
                  </button>
                  <input
                    ref={mapImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadMapImage}
                    className="hidden"
                  />
                </div>
                {newMap.image && (
                  <div className="bg-gray-700 p-2 rounded">
                    <img src={newMap.image} alt="Preview" className="w-full h-32 object-cover rounded" />
                  </div>
                )}
                {uploadStatus && (
                  <p className="text-xs text-center text-yellow-400 font-semibold">
                    {uploadStatus}
                  </p>
                )}
                <button onClick={addMap} className="w-full bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                  <Plus size={18} /> Add Map
                </button>
              </div>
              
              {/* Add Item Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Add New Item</h4>
                <input
                  type="text"
                  placeholder="Item name"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <div className="space-y-2">
                  <select
                    value={newItem.image}
                    onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                    className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                  >
                    <option value="">Select item icon</option>
                    {availableItemImages.map((img, idx) => (
                      <option key={idx} value={img.url}>{img.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => itemImageInputRef.current?.click()}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors text-sm"
                  >
                    + Upload New Item Icon
                  </button>
                  <input
                    ref={itemImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadItemImage}
                    className="hidden"
                  />
                </div>
                {newItem.image && (
                  <div className="bg-gray-700 p-2 rounded flex items-center justify-center">
                    <img src={newItem.image} alt="Preview" className="object-contain" style={{ minWidth: '30px', minHeight: '30px', maxWidth: '40px', maxHeight: '40px', width: '40px', height: '40px' }} />
                  </div>
                )}
                <button onClick={addItem} className="w-full bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                  <Plus size={18} /> Add Item
                </button>
              </div>

              {/* Add Marker Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Add Item To Map</h4>
                <select
                  value={newMarker.itemId}
                  onChange={(e) => setNewMarker({ ...newMarker, itemId: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                >
                  <option value="">Select item type</option>
                  {items.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {newMarker.itemId && items.find(item => item.id === parseInt(newMarker.itemId)) && (
                  <div className="bg-gray-700 p-2 rounded flex items-center justify-center">
                    <img src={items.find(item => item.id === parseInt(newMarker.itemId)).image} alt="Preview" className="object-contain" style={{ minWidth: '30px', minHeight: '30px', maxWidth: '40px', maxHeight: '40px', width: '40px', height: '40px' }} />
                  </div>
                )}
                <input
                  type="number"
                  placeholder="X position (0-100)"
                  value={newMarker.x}
                  onChange={(e) => setNewMarker({ ...newMarker, x: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <input
                  type="number"
                  placeholder="Y position (0-100)"
                  value={newMarker.y}
                  onChange={(e) => setNewMarker({ ...newMarker, y: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <div className="flex gap-2">
                  <button 
                    onClick={() => setIsPlacingMarker(!isPlacingMarker)}
                    className={`flex-1 ${isPlacingMarker ? 'bg-blue-700' : 'bg-blue-600'} hover:bg-blue-700 text-white p-2 rounded-lg transition-colors`}
                  >
                    {isPlacingMarker ? 'Cancel Click Mode' : 'Click on Map'}
                  </button>
                  <button onClick={addMarker} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                    <MapPin size={18} /> Add
                  </button>
                </div>
              </div>

              {/* Add Location Label Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Add Location Label</h4>
                <input
                  type="text"
                  placeholder="Location name"
                  value={newLabel.text}
                  onChange={(e) => setNewLabel({ ...newLabel, text: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <input
                  type="number"
                  placeholder="X position (0-100)"
                  value={newLabel.x}
                  onChange={(e) => setNewLabel({ ...newLabel, x: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <input
                  type="number"
                  placeholder="Y position (0-100)"
                  value={newLabel.y}
                  onChange={(e) => setNewLabel({ ...newLabel, y: e.target.value })}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 outline-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsPlacingLabel(!isPlacingLabel)}
                    className={`flex-1 ${isPlacingLabel ? 'bg-blue-700' : 'bg-blue-600'} hover:bg-blue-700 text-white p-2 rounded-lg transition-colors text-sm`}
                  >
                    {isPlacingLabel ? 'Cancel Click Mode' : 'Click on Map'}
                  </button>
                  <button onClick={addLabel} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                    <Plus size={18} /> Add Label
                  </button>
                </div>
              </div>

              {/* Edit/Move Markers Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Edit Markers</h4>
                <button 
                  onClick={() => {
                    setIsEditMode(!isEditMode);
                    setSelectedMarker(null);
                    setIsDraggingMarker(false);
                  }}
                  className={`w-full ${isEditMode ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white p-2 rounded-lg transition-colors font-semibold`}
                >
                  {isEditMode ? '✓ Edit Mode Active' : 'Enable Edit Mode'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  {isEditMode ? 'Click once to grab • Move mouse • Click again to drop • Double-click or Delete to remove' : 'Enable to move or delete markers on the map'}
                </p>
              </div>

              {/* Manage Items */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-white mb-3">Manage Items</h4>
                {items.map(item => (
                  <div key={item.id} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                    <div className="flex items-center gap-2 flex-1">
                      <img src={item.image} alt={item.name} className="object-contain" style={{ minWidth: '30px', minHeight: '30px', maxWidth: '40px', maxHeight: '40px', width: '40px', height: '40px' }} />
                      {editingItemId === item.id ? (
                        <input
                          type="text"
                          value={editingItemName}
                          onChange={(e) => setEditingItemName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveItemName(item.id);
                            if (e.key === 'Escape') cancelEditingItem();
                          }}
                          className="flex-1 px-2 py-1 bg-gray-600 text-white rounded border border-blue-500 outline-none"
                          autoFocus
                        />
                      ) : (
                        <span
                          className="text-white flex-1 cursor-pointer hover:text-blue-400"
                          onClick={() => startEditingItem(item)}
                        >
                          {item.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {editingItemId === item.id ? (
                        <>
                          <button
                            onClick={() => saveItemName(item.id)}
                            className="text-green-400 hover:text-green-300 px-2"
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelEditingItem}
                            className="text-gray-400 hover:text-gray-300 px-2"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button onClick={() => deleteItem(item.id)} className="text-red-400 hover:text-red-300">
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Manage Maps */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-white mb-3">Manage Maps</h4>
                {maps.map(map => (
                  <div key={map.id} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                    <div className="flex items-center gap-2 flex-1">
                      {editingMapId === map.id ? (
                        <input
                          type="text"
                          value={editingMapName}
                          onChange={(e) => setEditingMapName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveMapName(map.id);
                            if (e.key === 'Escape') cancelEditingMap();
                          }}
                          className="flex-1 px-2 py-1 bg-gray-600 text-white rounded border border-blue-500 outline-none"
                          autoFocus
                        />
                      ) : (
                        <span
                          className="text-white cursor-pointer hover:text-blue-400"
                          onClick={() => startEditingMap(map)}
                        >
                          {map.name}
                        </span>
                      )}
                      {map.id === currentMapId && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Default</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMapId === map.id ? (
                        <>
                          <button
                            onClick={() => saveMapName(map.id)}
                            className="text-green-400 hover:text-green-300 px-2"
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelEditingMap}
                            className="text-gray-400 hover:text-gray-300 px-2"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          {map.id !== currentMapId && (
                            <button
                              onClick={() => setCurrentMapId(map.id)}
                              className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded transition-colors"
                            >
                              Set Default
                            </button>
                          )}
                          {maps.length > 1 && (
                            <button onClick={() => deleteMap(map.id)} className="text-red-400 hover:text-red-300">
                              <X size={18} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Manage Markers */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-white mb-3">Manage Markers (Current Map)</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {markers.filter(m => m.mapId === currentMapId).map(marker => {
                    const item = getItemById(marker.itemId);
                    return (
                      <div key={marker.id} className="flex items-center justify-between bg-gray-700 p-2 rounded text-sm">
                        <div className="flex items-center gap-2">
                          {item && (
                            <img src={item.image} alt={item.name} className="object-contain" style={{ minWidth: '30px', minHeight: '30px', maxWidth: '40px', maxHeight: '40px', width: '40px', height: '40px' }} />
                          )}
                          <span className="text-white">{item?.name} ({marker.x.toFixed(1)}, {marker.y.toFixed(1)})</span>
                        </div>
                        <button onClick={() => deleteMarker(marker.id)} className="text-red-400 hover:text-red-300">
                          <X size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Manage Labels */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-white mb-3">Manage Labels (Current Map)</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {labels.filter(l => l.mapId === currentMapId).map(label => (
                    <div key={label.id} className="flex items-center justify-between bg-gray-700 p-2 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{label.text} ({label.x.toFixed(1)}, {label.y.toFixed(1)})</span>
                      </div>
                      <button onClick={() => deleteLabel(label.id)} className="text-red-400 hover:text-red-300">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Import/Export Section */}
              <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-white">Backup & Restore</h4>
                <div className="flex gap-2">
                  <button
                    onClick={exportData}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-1 transition-colors text-xs"
                  >
                    <Download size={16} /> Export
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg flex items-center justify-center gap-1 transition-colors text-xs"
                  >
                    <Upload size={16} /> Import
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={importData}
                  className="hidden"
                />
              </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Ad Banner - 728x90 */}
      {showBottomAd && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center p-2">
          <div className="relative flex items-center justify-center max-w-full">
            {/* Ad Content */}
            <div className="rounded flex items-center justify-center"
              style={{
                width: 'min(728px, calc(100vw - 60px))',
                height: '90px'
              }}
            >
              <img src={`${process.env.PUBLIC_URL}/ad/728x90.jpg`} alt="Ad" className="object-fill" />
            </div>
            {/* Close Button */}
            <button
              onClick={() => setShowBottomAd(false)}
              className="absolute -top-1 -right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg transition-colors"
              title="Close ad"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Right Side Ad - 336x280 */}
      {showRightAd && (
        <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 p-2">
          <div className="relative">
            {/* Ad Content */}
            <div className="bg-gray-700 rounded flex items-center justify-center"
              style={{
                width: '336px',
                height: '280px'
              }}
            >
              <img src={`${process.env.PUBLIC_URL}/ad/juice.jpg`} alt="Ad" className="object-fill" />
            </div>
            {/* Close Button */}
            <button
              onClick={() => setShowRightAd(false)}
              className="absolute -top-1 -right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg transition-colors"
              title="Close ad"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}